import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FileExplorerservice } from '../file-explorer/file-explorer.service';
import { Archivo } from '../file-explorer/FileNode.model';
import { formatDateString, formatDateStringWithTime } from '../core/helpers/utils';
import { getCurrentLanguage } from '../core/helpers/utils';
import { BuscadorArchivosTranslations, buscadorArchivosTranslations } from './buscador-archivos-translations';


// types
import { SortEvent } from 'src/app/shared/advanced-table/sortable.directive';
import { Column } from 'src/app/shared/advanced-table/advanced-table.component';

@Component({
  selector: 'app-buscador-archivos',
  templateUrl: './buscador-archivos.component.html',
  styleUrls: ['./buscador-archivos.component.scss']
})
export class BuscadorArchivosComponent implements OnInit {
  @Input() idExpediente!: string; // Recibe el ID de expediente
  @Output() navigateToFolder = new EventEmitter<string>();
  records: Archivo[] = [];
  columns: Column[] = [];
  pageSizeOptions: number[] = [5, 10, 25, 50, 100];
  defaultPageSize: number = 5;

  currentLanguage: string = getCurrentLanguage()

  loaded: boolean = false;
  error: string = '';
  allDataSearch: Archivo[] = [];

  constructor(private fileExplorerService: FileExplorerservice) { }

  ngOnInit(): void {
    if (this.idExpediente) {
      this.fetchFiles();
    } else {
      this.error = 'ID de expediente no proporcionado.';
    }
    this.initTableConfig();
  }

  /**
   * Fetch files from the service and flatten the structure
   */
  fetchFiles(): void {
    const tipoExplorador = 'expediente';

    this.fileExplorerService.getPlantillaCarpetas(this.idExpediente, tipoExplorador).subscribe(
      (data) => {
        this.records = this.flattenFileStructure(data).sort((a, b) =>
          new Date(b.fecha_subida).getTime() - new Date(a.fecha_subida).getTime()
        );
        this.allDataSearch = this.records; // Save a copy of all data for search
        this.loaded = true;
      },
      (error: any) => {
        this.error = error.message || 'Error loading files';
        this.loaded = false;
      }
    );
  }

  /**
   * Recursively flatten file structure and collect all files
   */
  /**
 * Recursively flatten file structure and collect all files
 * Limits the path length to fit within MAX_PATH_LENGTH characters
 */
  flattenFileStructure(data: any[]): Archivo[] {
    const files: Archivo[] = [];
    const MAX_PATH_LENGTH = 55; // Máximo de caracteres permitidos
    const RESERVED_FOR_ELLIPSIS = 3; // Reservamos espacio adicional para "..."

    const truncatePath = (fullPath: string): string => {
      const pathSegments = fullPath.split(' > ');

      // Si toda la ruta entra dentro del límite, retornarla sin modificaciones
      if (fullPath.length <= MAX_PATH_LENGTH) {
        return fullPath;
      }

      // Si el último nodo es más largo que el espacio disponible, truncarlo directamente
      const lastSegment = pathSegments[pathSegments.length - 1];
      if (lastSegment.length > MAX_PATH_LENGTH - RESERVED_FOR_ELLIPSIS) {
        return `... > ${lastSegment.substring(0, MAX_PATH_LENGTH - RESERVED_FOR_ELLIPSIS - 3)}...`;
      }

      let currentLength = RESERVED_FOR_ELLIPSIS; // Espacio reservado para "... >"
      const visibleSegments: string[] = [];

      // Construir la ruta visible respetando el límite
      while (pathSegments.length > 0) {
        const segment = pathSegments.pop()!;
        const segmentLength = segment.length + 3; // Incluye el separador " > "

        if (currentLength + segmentLength <= MAX_PATH_LENGTH) {
          visibleSegments.unshift(segment);
          currentLength += segmentLength;
        } else {
          break;
        }
      }

      // Si no cabe ningún segmento, muestra el último truncado con "... >"
      if (visibleSegments.length === 0) {
        return `... > ${lastSegment.substring(0, MAX_PATH_LENGTH - RESERVED_FOR_ELLIPSIS - 3)}...`;
      }

      // Formar la ruta truncada con "..." al inicio si se truncaron segmentos
      return `... > ${visibleSegments.join(' > ')}`;
    };

    const traverse = (nodes: any[], currentPath: string = '') => {
      nodes.forEach((node) => {
        const newPath = currentPath ? `${currentPath} > ${node.nombre}` : node.nombre;

        // Procesar archivos de la carpeta actual
        if (node.archivos && node.archivos.length > 0) {
          node.archivos.forEach((archivo: Archivo) => {
            archivo['path'] = truncatePath(newPath); // Aplicar truncado al path
            archivo['folderId'] = node.id; // Asignar el ID de la carpeta contenedora
          });
          files.push(...node.archivos);
        }

        // Recorrer hijos si existen
        if (node.hijos && node.hijos.length > 0) {
          traverse(node.hijos, newPath); // Continuar con los hijos
        }
      });
    };

    traverse(data);
    return files;
  }




  /**
   * Initialize table columns
   */
  initTableConfig(): void {
    let currentLanguage: string = getCurrentLanguage()
    const currentTranslations = buscadorArchivosTranslations[currentLanguage].TABLE;
    this.columns = [
      {
        name: 'accionesBuscadorArchivos',
        label: currentTranslations.ACTIONS,
        formatter: () => '',
        width: 10,
        sort: false,
      },
      {
        name: 'nombre',
        label: currentTranslations.NAME,
        formatter: (record: Archivo) => record.nombre,
        width: 220
      },
      {
        name: 'path',
        label: currentTranslations.PATH,
        formatter: (record: Archivo) => record['path'],
        width: 280
      },
      {
        name: 'usuario_nombre',
        label: currentTranslations.USER_NAME,
        formatter: (record: Archivo) => `${record.usuario_nombre} ${record.usuario_apellidos}`,
        width: 50
      },
      {
        name: 'fecha_subida',
        label: currentTranslations.UPLOAD_DATE,
        formatter: (record: Archivo) => formatDateStringWithTime(record.fecha_subida ?? '', this.currentLanguage),
        width: 50
      }
    ];
  }

  /**
   * Search table data
   */
  searchData(searchTerm: string): void {
    if (searchTerm === '') {
      this.records = this.allDataSearch;
    } else {
      this.records = this.allDataSearch.filter(record => {
        const fullName = `${record.usuario_nombre} ${record.usuario_apellidos}`.toLowerCase();
        return (
          record.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (record.path?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
          fullName.includes(searchTerm.toLowerCase()) // Concatenación para buscar en nombre + apellidos
        );
      });
    }
  }

  /**
   * Sort table data
   */
  onSort(event: SortEvent): void {
    if (event.direction === '') {
      this.records = this.allDataSearch; // Restaurar los datos originales
    } else {
      this.records = [...this.records].sort((a, b) => {
        const column = event.column as keyof Archivo;

        // Manejo seguro de valores undefined
        const valueA = a[column] ?? '';
        const valueB = b[column] ?? '';

        // Comparar valores
        const res = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;

        // Devolver el resultado en función de la dirección
        return event.direction === 'asc' ? res : -res;
      });
    }
  }

  /// Método para manejar acciones desde advanced-table
  onTableAction(event: { action: string; folderId: string }): void {
    if (event.action === 'navigateToFolder') {
      this.navigateToFolder.emit(event.folderId); // Emitimos el folderId recibido
    }
  }

}
